import { type GridAggregationModel, useGridApiContext } from '@mui/x-data-grid-premium';
import { useEffect, useState } from 'react';
import type { BaseGridStateWithHelpers } from '../shared/baseGridTypes';
import { stripEmptyFilters } from '../shared/filterSanitize';
import {
  dataGridStateToConfig,
  emptyGridState,
  useViewConfigStore,
  type ViewConfigPayload,
  type ViewConfigRow,
} from './viewConfig.store';
import { useViewConfigurationList } from './viewConfigurationReads';
import {
  useCreateViewConfiguration,
  useDeleteViewConfiguration,
  useUpdateViewConfiguration,
} from './viewConfigurationWrites';

/**
 * Saved named views for one grid page.
 *
 *   apiRef.exportState() ──dataGridStateToConfig──▶ ViewConfiguration.config
 *          + quickFilter · serverFilters                    │  (server)
 *                                                           ▼
 *   apiRef.restoreState() ◄── config ── select a view ──────┘
 *          + setQuickFilterOption · updateFilters
 *
 * The selected view lives in `viewConfig.store` (persisted per storageKey); the
 * LIST is local React state seeded from `useViewConfigurationList` (every page of
 * `viewConfigurationPage`, P7-R35) so create/rename/delete show up without a refetch.
 */
export type GridViewConfig = {
  views: ViewConfigRow[];
  selectedView: ViewConfigRow | null | undefined;
  /** True once the selected view still exists in the list — gates "Save changes". */
  canSaveChanges: boolean;
  selectView: (view: ViewConfigRow) => void;
  /** Reset the grid and the server-side filters, and clear the selection. */
  selectDefaultView: () => void;
  saveChangesToSelectedView: () => void;

  createDialogOpen: boolean;
  openCreateDialog: () => void;
  closeCreateDialog: () => void;
  confirmCreate: (name: string) => Promise<void>;

  viewToRename: ViewConfigRow | null;
  startRename: (view: ViewConfigRow) => void;
  cancelRename: () => void;
  confirmRename: (name: string) => void;

  viewToDelete: ViewConfigRow | null;
  startDelete: (view: ViewConfigRow) => void;
  cancelDelete: () => void;
  confirmDelete: () => void;
};

export const buildRestoreState = (
  gridExport: { aggregation?: { model?: GridAggregationModel } } & Record<string, unknown>,
  aggregationModel?: GridAggregationModel
) =>
  aggregationModel
    ? { ...gridExport, aggregation: { model: { ...gridExport.aggregation?.model, ...aggregationModel } } }
    : gridExport;

export function useGridViewConfig({
  page,
  enhancedGridState,
  aggregationModel,
}: {
  page: string;
  enhancedGridState?: BaseGridStateWithHelpers;
  /**
   * Forced over the saved view's aggregation model on every restore, so a stale/unsafe entry saved
   * before a fix shipped (e.g. TAS-3328's repackDate:'max') can't resurrect on reload.
   */
  aggregationModel?: GridAggregationModel;
}): GridViewConfig {
  const { create: createViewConfiguration } = useCreateViewConfiguration();
  const { update: updateViewConfiguration } = useUpdateViewConfiguration();
  const { remove: deleteViewConfiguration } = useDeleteViewConfiguration();
  const setConfig = useViewConfigStore((state) => state._setConfig);
  const config = useViewConfigStore((state) => state.view);
  const apiRef = useGridApiContext();
  const [viewConfigs, setViewConfigs] = useState<ViewConfigRow[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewToRename, setViewToRename] = useState<ViewConfigRow | null>(null);
  const [viewToDelete, setViewToDelete] = useState<ViewConfigRow | null>(null);

  const savedViews = useViewConfigurationList(page);

  // Sync the complete list to local state when it loads
  useEffect(() => {
    if (savedViews) {
      setViewConfigs(savedViews);
    }
  }, [savedViews]);

  const buildConfigToExport = () => ({
    ...dataGridStateToConfig(apiRef.current.exportState()),
    quickFilter: enhancedGridState?.quickFilterOption?.label,
    serverFilters: stripEmptyFilters(enhancedGridState?.gridFilters),
  });

  const restoreState = (config: ViewConfigPayload) => {
    const { quickFilter, serverFilters, ...gridExport } = config;
    if (enhancedGridState) {
      enhancedGridState.setQuickFilterOption(
        enhancedGridState.quickFilterOptions?.find((f) => f.label === quickFilter)
      );
      enhancedGridState.updateFilters(stripEmptyFilters(serverFilters));
    }
    // When a static aggregation model is supplied, force it over the saved view's aggregation so a pre-fix
    // unsafe entry (e.g. repackDate:'max', which crashes the XLS export — TAS-3328) can't resurrect on
    // reload. Merge (not wholesale replace) so user aggregations on non-static columns survive.
    apiRef.current.restoreState(buildRestoreState(gridExport, aggregationModel));
  };

  return {
    views: viewConfigs,
    selectedView: config,
    canSaveChanges: !!viewConfigs.find((v) => v.id === config?.id),

    selectView: (view) => {
      restoreState(view.config);
      setConfig(view);
    },

    selectDefaultView: () => {
      apiRef.current.restoreState({ ...emptyGridState });
      enhancedGridState?.resetToDefault();
      setConfig(null);
    },

    saveChangesToSelectedView: () => {
      if (!config) return;
      const updatedConfig = buildConfigToExport();
      updateViewConfiguration({ id: config.id, input: { config: updatedConfig } });
      setViewConfigs(viewConfigs.map((v) => (v.id === config.id ? { ...v, config: updatedConfig } : v)));
    },

    createDialogOpen,
    openCreateDialog: () => setCreateDialogOpen(true),
    closeCreateDialog: () => setCreateDialogOpen(false),
    confirmCreate: async (name) => {
      setCreateDialogOpen(false);
      const { data } = await createViewConfiguration({ name, config: buildConfigToExport(), page });
      const newView = data.createViewConfiguration;
      setConfig(newView);
      setViewConfigs([...viewConfigs, newView]);
    },

    viewToRename,
    startRename: (view) => setViewToRename(view),
    cancelRename: () => setViewToRename(null),
    confirmRename: (name) => {
      if (!viewToRename) return;
      updateViewConfiguration({ id: viewToRename.id, input: { name } });
      setViewConfigs(viewConfigs.map((v) => (v.id === viewToRename.id ? { ...v, name } : v)));
      if (config?.id === viewToRename.id) {
        setConfig({ ...config, name });
      }
      setViewToRename(null);
    },

    viewToDelete,
    startDelete: (view) => setViewToDelete(view),
    cancelDelete: () => setViewToDelete(null),
    confirmDelete: () => {
      if (!viewToDelete) return;
      setViewConfigs(viewConfigs.filter((v) => v.id !== viewToDelete.id));
      deleteViewConfiguration(viewToDelete.id);
      setViewToDelete(null);
      if (viewToDelete.id === config?.id) {
        apiRef.current.restoreState({ ...emptyGridState });
        enhancedGridState?.resetToDefault();
        setConfig(null);
      }
    },
  };
}
